import { SERIES_TEXT } from 'features/Series/series.constants'

// Copy for the Series pages, by area; "(ours)" strings are not from the design board.
export const SERIES_PAGES_TEXT = {
  menu: {
    button: 'Scheduling',
    calendar: 'Calendar',
    calendarSub: 'Day and week schedule',
    series: 'Series',
    seriesSub: 'Every appointment series',
  },
  gate: {
    loading: 'Loading',
  },
  list: {
    title: 'Appointment Series',
    manageTemplates: 'Manage series templates',
    stats: {
      total: 'Total series',
      active: 'Active',
      completed: 'Completed',
      cancelled: 'Cancelled',
      upcomingVisits: 'Upcoming visits',
    },
    filters: {
      provider: 'Provider',
      allProviders: 'All providers',
      status: 'Status',
      statusActive: 'Active',
      statusCompleted: 'Completed',
      statusCancelled: 'Cancelled',
      statusAll: 'All',
      search: 'Search',
      searchPlaceholder: 'Last, First or PID…',
      searchButton: 'Search',
      reset: 'Reset',
    },
    columns: [
      'Patient',
      'Provider',
      'Treatment',
      'Cadence',
      'Progress',
      'Next visit',
      'Status',
      'Actions',
    ],
    unknownPatient: 'Unknown patient',
    noDate: '—',
    visitsLabel: (total: number) => `${total} visits`,
    cadenceTimes: (label: string, total: number) => `${label} × ${total}`,
    progressLabel: (completed: number, total: number) => `${completed} of ${total}`,
    pid: (pid: number | string) => `PID ${pid}`,
    details: 'Details',
    cancel: 'Cancel',
    pillActive: 'Active',
    pillCompleted: 'Completed',
    pillCancelled: 'Cancelled',
    empty: 'No series match these filters.',
    error: 'Series are unavailable. OpenEMR did not answer.',
    errorAccessDenied: SERIES_TEXT.emrAccessDenied,
    tryAgain: 'Try again',
    showing: (shown: number, total: number) => `Showing ${shown} of ${total} series`,
  },
  templates: {
    title: 'Series templates',
    back: '← Back to Series',
    stats: { templates: 'Templates', available: 'Available', retired: 'Retired' },
    add: 'Add a series template',
    info: 'A template can be edited at any time, even one in use. Changes apply to series built from then on; a series already booked keeps the copy it was built with. Retire a template to take it out of the appointment modal without touching anything booked.',
    columns: [
      'Series',
      'Visits',
      'Cadence',
      'Visit length',
      'Category',
      'Facility',
      'Billing code',
      'In use',
      'Status',
      'Actions',
    ],
    noValue: '—',
    anyFacility: 'Any',
    visitLength: (minutes: number) => `${minutes} mins`,
    inUse: (n: number) => (n > 0 ? 'keeping their own copy' : 'not used yet'),
    pillAvailable: 'Available',
    pillRetired: 'Retired',
    edit: 'Edit',
    duplicate: 'Duplicate',
    retire: 'Retire',
    restore: 'Restore',
    empty: 'No series templates yet.',
    error: 'Templates are unavailable. OpenEMR did not answer.',
    errorAccessDenied: SERIES_TEXT.emrAccessDenied,
    tryAgain: 'Try again',
    retireTitle: 'Retire for every site?',
    retireWarning:
      'This template is shared by every site. Retiring it takes it out of the appointment modal everywhere.',
    duplicateName: (name: string) => `Copy of ${name}`.slice(0, 150),
  },
  modal: {
    titleAdd: 'Add a series template',
    titleEdit: 'Edit series template',
    inUseBadge: (n: number) => `In use by ${n} series · they keep their own copy`,
    sharedLine: 'This template is shared by everyone on this EMR.',
    name: 'Name',
    namePlaceholder: 'e.g. Wound care',
    description: 'Description',
    visits: 'Visits',
    visitsHint: 'Nr of visits in this course',
    cadence: 'Cadence',
    every: 'Every',
    cadenceHint: 'Spacing between visits',
    visitLength: 'Visit length',
    minutes: 'minutes',
    visitLengthHint: 'Starting point only. The real length can be changed on each appointment.',
    fewerVisits: 'Fewer visits',
    moreVisits: 'More visits',
    lessOften: 'Less often',
    moreOften: 'More often',
    shorter: 'Shorter',
    longer: 'Longer',
    category: 'Category',
    categoryNone: 'None',
    categoryHint: 'Optional',
    billingCode: 'Billing code',
    billingNone: 'None',
    pickCode: 'Pick a code',
    billingHint: 'The treatment billed at each visit.',
    medicalIssue: 'Medical issue',
    issuePlaceholder: 'Type to find a problem',
    addAnother: '+ Add another',
    clear: 'Clear',
    facility: 'Facility',
    facilityAny: 'Any',
    available: 'Available',
    availableCheckbox: 'Show it in the “Series template” list of the appointment modal',
    footerNote: (visits: number, cadence: string, minutes: number) =>
      `${visits} visits · ${cadence.toLowerCase()} · ${minutes} min each`,
    cancel: 'Cancel',
    save: 'Save',
    saveAsNew: 'Save as New',
    newName: (name: string) => `${name} (new)`.slice(0, 150),
  },
  toasts: {
    noVisitBooked: 'This series has no visit booked in Residen yet.',
    seriesGone: 'This series is no longer available.',
    emrNoAnswer: 'OpenEMR did not answer. Try again.',
    visitNotOnCalendar: 'This visit is not on the calendar any more.',
    cancelTitle: 'Cancel this series?',
    cancelWarning: 'The booked visits stay on the calendar. Cancel them one by one if needed.',
    seriesCancelled: 'Series cancelled.',
    cancelEmrNoAnswer: 'OpenEMR did not answer. Reload to see what was saved.',
    templateGone: 'This template is no longer available.',
    templateEmrNoAnswer: 'OpenEMR did not answer. Nothing was retried.',
    emrAccessDenied: SERIES_TEXT.emrAccessDenied,
  },
} as const
